// ============================================================
// FILE LOCATION: src/components/TimelineBuilder/TimelineEventCard.tsx
// ============================================================

/**
 * TimelineEventCard — Option A Roadmap Redesign
 *
 * Visual changes from previous version:
 *   - Date gutter left of the timeline rail
 *   - Small uniform dots (lighter teal for done, slate for incomplete, amber for high-impact only)
 *   - Tick connectors (3 small gray dashes) between dots
 *   - Status shown via inline checkmark or high-impact badge
 *   - Expanded editing form preserved
 */

import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  TimelineEvent,
  TimelineEventCardProps,
  EVENT_TYPE_OPTIONS,
  OUTCOME_OPTIONS,
  EVIDENCE_FLAG_OPTIONS,
  FUNCTION_TAG_OPTIONS,
  EvidenceFlag,
  FunctionTag,
} from "./types";
import { parseDateInput } from "./utils";
import { toDisplayEvent } from "./displayAdapter";

// ── Tiny icon components ────────────────────────────────────

const ChevronDown = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const ChevronUp = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

const ChevronRight = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 18l6-6-6-6" />
  </svg>
);

const DuplicateIcon = ({ className = "w-3.5 h-3.5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
  </svg>
);

const TrashIcon = ({ className = "w-3.5 h-3.5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
  </svg>
);

const PlusIcon = ({ className = "w-3.5 h-3.5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const EditIcon = ({ className = "w-3.5 h-3.5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
  </svg>
);

const MoreHorizIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />
  </svg>
);

const CheckIcon = ({ className = "w-3.5 h-3.5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 6L9 17l-5-5" />
  </svg>
);

const AlertDotIcon = ({ className = "w-3 h-3" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01" />
  </svg>
);

// ── Event type vector icons ─────────────────────────────────

const EVENT_TYPE_ICONS: Record<string, React.ReactNode> = {
  SYMPTOM: <path d="M3 12h4l3-9 4 18 3-9h4" />,
  DIAGNOSIS: (
    <>
      <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 14l2 2 4-4" />
    </>
  ),
  MEDICATION: (
    <>
      <path d="M10.5 3.5a4.95 4.95 0 017.07 7.07l-7.07 7.07a4.95 4.95 0 01-7.07-7.07l7.07-7.07z" />
      <path d="M7 14l7-7" />
    </>
  ),
  CONSERVATIVE_THERAPY: (
    <>
      <path d="M12 3l7 3v5c0 4.5-3 8.25-7 9.5-4-1.25-7-5-7-9.5V6l7-3z" />
      <path d="M12 10v4m-2-2h4" />
    </>
  ),
  PROCEDURE: (
    <>
      <path d="M18.4 5.6a1.5 1.5 0 010 2.12L9.53 16.6a4 4 0 01-2.83 1.17H4.5v-2.2a4 4 0 011.17-2.83l8.88-8.88a1.5 1.5 0 012.12 0l1.73 1.74z" />
      <path d="M15 8l1 1" />
    </>
  ),
  REQUEST: (
    <>
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
      <path d="M14 2v6h6" />
      <path d="M12 18v-6m0 0l-3 3m3-3l3 3" />
    </>
  ),
  DENIAL: (
    <>
      <path d="M12 3l7 3v5c0 4.5-3 8.25-7 9.5-4-1.25-7-5-7-9.5V6l7-3z" />
      <path d="M10 10l4 4m0-4l-4 4" />
    </>
  ),
  FUNCTION_CHANGE: <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
  IMAGING: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="12" cy="12" r="3" />
      <path d="M3 8h18" />
    </>
  ),
  HOSPITALIZATION: (
    <>
      <rect x="4" y="6" width="16" height="15" rx="2" />
      <path d="M9 21V6m6 0v15" />
      <path d="M4 11h16" />
      <path d="M10 2h4a1 1 0 011 1v3H9V3a1 1 0 011-1z" />
    </>
  ),
  SPECIALIST_VISIT: (
    <>
      <path d="M16 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="10" cy="7" r="4" />
      <path d="M19 8v3m0 0a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" />
    </>
  ),
};

const DEFAULT_TYPE_ICON = <circle cx="12" cy="12" r="4" />;

const EventTypeIcon: React.FC<{
  type: string;
  className?: string;
}> = ({ type, className = "w-4 h-4" }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {EVENT_TYPE_ICONS[type] || DEFAULT_TYPE_ICON}
  </svg>
);

// ── Custom type selector (unchanged) ────────────────────────

const TypeSelect: React.FC<{
  value: string;
  onChange: (value: string) => void;
  id?: string;
}> = ({ value, onChange, id }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const selected = EVENT_TYPE_OPTIONS.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") { setOpen(false); return; }
      if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
        e.preventDefault(); setOpen(true); return;
      }
      if (open) {
        const idx = EVENT_TYPE_OPTIONS.findIndex((o) => o.value === value);
        if (e.key === "ArrowDown") {
          e.preventDefault();
          onChange(EVENT_TYPE_OPTIONS[(idx + 1) % EVENT_TYPE_OPTIONS.length].value);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          onChange(EVENT_TYPE_OPTIONS[(idx - 1 + EVENT_TYPE_OPTIONS.length) % EVENT_TYPE_OPTIONS.length].value);
        } else if (e.key === "Enter" || e.key === " ") {
          e.preventDefault(); setOpen(false);
        }
      }
    },
    [open, value, onChange]
  );

  return (
    <div ref={containerRef} className="relative" id={id}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        onKeyDown={handleKeyDown}
        className="
          w-full flex items-center gap-2
          border border-slate-200 rounded-lg px-2.5 py-2 text-sm text-slate-700
          hover:border-slate-300
          focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400
          transition-all duration-150 text-left
        "
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        {selected && (
          <EventTypeIcon type={selected.value} className="w-4 h-4 text-slate-500 flex-shrink-0" />
        )}
        <span className="flex-1 truncate">{selected?.label || "Select type"}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-slate-400 flex-shrink-0 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-xl shadow-lg py-1 max-h-56 overflow-y-auto"
          style={{ scrollbarWidth: "thin" }}
        >
          {EVENT_TYPE_OPTIONS.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <li
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                onClick={() => { onChange(opt.value); setOpen(false); }}
                className={`
                  flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer transition-colors duration-100
                  ${isSelected ? "bg-teal-50 text-teal-700" : "text-slate-700 hover:bg-slate-50"}
                `}
              >
                <EventTypeIcon type={opt.value} className={`w-4 h-4 flex-shrink-0 ${isSelected ? "text-teal-600" : "text-slate-400"}`} />
                <span className={isSelected ? "font-medium" : ""}>{opt.label}</span>
                {isSelected && (
                  <svg className="w-4 h-4 ml-auto text-teal-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// ── Delete confirmation popover (unchanged) ─────────────────

const DeleteConfirm: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ onConfirm, onCancel }) => (
  <div className="absolute right-0 bottom-full mb-1.5 z-20">
    <div className="bg-white border border-slate-200 rounded-xl shadow-lg px-3 py-2.5 flex items-center gap-2 whitespace-nowrap">
      <span className="text-xs text-slate-600">Delete this event?</span>
      <button
        onClick={(e) => { e.stopPropagation(); onConfirm(); }}
        className="px-2.5 py-1 text-xs font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
      >
        Delete
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onCancel(); }}
        className="px-2.5 py-1 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
      >
        Cancel
      </button>
    </div>
  </div>
);

// ── Mobile overflow menu (unchanged) ────────────────────────

const OverflowMenu: React.FC<{
  onEdit: () => void;
  onAddDetails: () => void;
  onDuplicate?: () => void;
  onRequestDelete: () => void;
}> = ({ onEdit, onAddDetails, onDuplicate, onRequestDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const menuItem = "w-full flex items-center gap-2.5 px-3 py-2 text-xs transition-colors";

  return (
    <div ref={ref} className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100 transition-colors"
        aria-label="More actions"
      >
        <MoreHorizIcon className="w-4 h-4" />
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-xl shadow-lg py-1 min-w-[140px]">
          <button onClick={(e) => { e.stopPropagation(); onEdit(); setIsOpen(false); }} className={`${menuItem} text-slate-700 hover:bg-slate-50`}>
            <EditIcon className="w-3.5 h-3.5 text-slate-400" /> Edit
          </button>
          <button onClick={(e) => { e.stopPropagation(); onAddDetails(); setIsOpen(false); }} className={`${menuItem} text-slate-700 hover:bg-slate-50`}>
            <PlusIcon className="w-3.5 h-3.5 text-slate-400" /> Add details
          </button>
          {onDuplicate && (
            <button onClick={(e) => { e.stopPropagation(); onDuplicate(); setIsOpen(false); }} className={`${menuItem} text-slate-700 hover:bg-slate-50`}>
              <DuplicateIcon className="w-3.5 h-3.5 text-slate-400" /> Duplicate
            </button>
          )}
          <div className="border-t border-slate-100 my-1" />
          <button onClick={(e) => { e.stopPropagation(); onRequestDelete(); setIsOpen(false); }} className={`${menuItem} text-red-600 hover:bg-red-50`}>
            <TrashIcon className="w-3.5 h-3.5" /> Delete
          </button>
        </div>
      )}
    </div>
  );
};

// ── Type-aware placeholder map ──────────────────────────────

const TYPE_PLACEHOLDERS: Record<string, string> = {
  SYMPTOM: "Symptom or complaint…",
  DIAGNOSIS: "Diagnosis or condition…",
  MEDICATION: "Medication started or changed…",
  CONSERVATIVE_THERAPY: "Therapy or treatment tried…",
  PROCEDURE: "Procedure or intervention…",
  REQUEST: "Authorization request…",
  DENIAL: "Denial or appeal…",
  FUNCTION_CHANGE: "Impact on daily function…",
  IMAGING: "Imaging or test result…",
  HOSPITALIZATION: "Admission or visit…",
  SPECIALIST_VISIT: "Specialist consult…",
};

// ── Tick connector component ────────────────────────────────

const TickConnector = ({ visible = true }: { visible?: boolean }) => (
  <div
    className="flex flex-col items-center gap-[3px] py-[3px]"
    style={{ visibility: visible ? "visible" : "hidden" }}
  >
    <div className="w-[2px] h-1 rounded-sm bg-slate-300" />
    <div className="w-[2px] h-1 rounded-sm bg-slate-300" />
    <div className="w-[2px] h-1 rounded-sm bg-slate-300" />
  </div>
);

// ── Suggestion text for attention events ────────────────────

const ATTENTION_HINTS: Record<string, string> = {
  SYMPTOM: "Add onset date to establish chronicity",
  FUNCTION_CHANGE: "Top denial reason — add ADL impact",
  CONSERVATIVE_THERAPY: "Add duration & outcome details",
  IMAGING: "Add prior imaging results",
};

// Event types whose incompleteness has outsized impact on denial outcomes.
// Only these get amber treatment; all other incomplete events stay neutral slate.
const HIGH_IMPACT_TYPES = new Set(["FUNCTION_CHANGE"]);

// ═══════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════

const TimelineEventCard: React.FC<TimelineEventCardProps> = ({
  event,
  index,
  onUpdate,
  onDelete,
  onDuplicate,
  isFirst,
  isLast,
  readOnly = false,
}) => {
  const isTrulyEmpty = !event.title?.trim() && !event.start_date && !event.date_label;
  const [isExpanded, setIsExpanded] = useState(event.isNew && isTrulyEmpty ? true : false);
  const [dateInput, setDateInput] = useState(event.date_label || event.start_date || "");
  const [supportingOpen, setSupportingOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const titleRef = useRef<HTMLInputElement>(null);

  const display = toDisplayEvent(event);
  const eventTypeOption = EVENT_TYPE_OPTIONS.find((opt) => opt.value === event.event_type);

  // Focus title on expand
  useEffect(() => {
    if (isExpanded && titleRef.current) {
      const timer = setTimeout(() => titleRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  // ── Quick action handlers ──────────────────────────────

  const handleEdit = useCallback(() => { setIsExpanded(true); }, []);
  const handleAddDetails = useCallback(() => { setIsExpanded(true); setSupportingOpen(true); }, []);

  // ── Field handlers ─────────────────────────────────────

  const handleFieldChange = useCallback(
    (field: keyof TimelineEvent, value: unknown) => {
      onUpdate({ ...event, [field]: value, isNew: false });
    },
    [event, onUpdate]
  );

  const handleDateChange = useCallback(
    (input: string) => {
      setDateInput(input);
      const parsed = parseDateInput(input);
      onUpdate({
        ...event,
        date_precision: parsed.precision,
        start_date: parsed.startDate,
        end_date: parsed.endDate,
        date_label: parsed.dateLabel,
        relative_value: parsed.relativeValue,
        isNew: false,
      });
    },
    [event, onUpdate]
  );

  const handleEvidenceFlagToggle = useCallback(
    (flag: EvidenceFlag) => {
      const currentFlags = event.evidence_flags || [];
      const newFlags = currentFlags.includes(flag)
        ? currentFlags.filter((f) => f !== flag)
        : [...currentFlags, flag];
      handleFieldChange("evidence_flags", newFlags);
    },
    [event.evidence_flags, handleFieldChange]
  );

  const handleFunctionTagToggle = useCallback(
    (tag: FunctionTag) => {
      const currentTags = event.function_tags || [];
      const newTags = currentTags.includes(tag)
        ? currentTags.filter((t) => t !== tag)
        : [...currentTags, tag];
      handleFieldChange("function_tags", newTags);
    },
    [event.function_tags, handleFieldChange]
  );

  const handleDelete = useCallback(() => {
    onDelete(event.id);
    setConfirmDelete(false);
  }, [event.id, onDelete]);

  const handleDuplicate = useCallback(() => {
    onDuplicate?.(event);
  }, [event, onDuplicate]);

  // ── Computed ───────────────────────────────────────────

  const supportingCount =
    (event.outcome && event.outcome !== "PENDING" ? 1 : 0) +
    (event.evidence_flags?.length || 0) +
    (event.function_tags?.length || 0);

  // Completion states
  const hasTitle = !!event.title?.trim();
  const hasDate = !!(event.start_date || event.date_label || event.relative_value);
  const hasDetails = !!(event.details?.trim());
  const hasOutcome = !!(event.outcome && event.outcome !== "PENDING");
  const hasEvidence = !!(event.evidence_flags && event.evidence_flags.length > 0);
  const filledFieldCount = [hasDate, hasDetails, hasOutcome, hasEvidence].filter(Boolean).length;

  type CompletionState = "complete" | "attention" | "empty";
  const completionState: CompletionState =
    hasTitle && filledFieldCount >= 1 ? "complete"
    : hasTitle ? "attention"
    : "empty";

  // Date display for the gutter
  const dateDisplay = display.displayDate && display.displayDate !== "Date TBD"
    ? display.displayDate
    : null;

  // Attention hint
  const attentionHint = completionState === "attention"
    ? ATTENTION_HINTS[event.event_type] || "Add more details to strengthen your case"
    : null;

  // High-impact: only these get amber treatment when incomplete
  const isHighImpact = completionState === "attention" && HIGH_IMPACT_TYPES.has(event.event_type);

  const detailsLength = event.details?.length || 0;
  const showCharCount = detailsLength > 180;

  // ═══════════════════════════════════════════════════════
  // Render
  // ═══════════════════════════════════════════════════════

  return (
    <div
      className="relative flex group/card"
      style={{
        animation: `slideIn 0.3s ease forwards`,
        animationDelay: `${index * 0.05}s`,
        opacity: 0,
      }}
    >
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-6px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes markerPing {
          0%, 100% { box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(217,119,6,0); }
          50% { box-shadow: 0 0 0 2px white, 0 0 0 6px rgba(217,119,6,0.1); }
        }
      `}</style>

      {/* ── Date gutter ────────────────────────────────── */}
      <div className="w-[52px] flex-shrink-0 flex justify-end pt-[7px] pl-2 pr-0">
        {dateDisplay ? (
          <span
            className="font-mono text-[10.5px] font-medium text-slate-400 leading-none text-right whitespace-nowrap"
          >
            {dateDisplay}
          </span>
        ) : completionState !== "empty" ? (
          <button
            onClick={() => !readOnly && setIsExpanded(true)}
            className="font-sans text-[10px] font-medium text-slate-400 leading-none cursor-pointer hover:text-slate-600 transition-colors"
          >
            + date
          </button>
        ) : (
          <span className="font-mono text-[10.5px] text-slate-300 leading-none">—</span>
        )}
      </div>

      {/* ── Track column: connectors + dot ────────────── */}
      <div className="w-9 flex flex-col items-center flex-shrink-0">
        <TickConnector visible={!isFirst} />

        {/* Dot */}
        <div
          className={`
            w-[10px] h-[10px] rounded-full flex-shrink-0 transition-transform duration-200
            group-hover/card:scale-125
            ${completionState === "complete"
              ? "bg-teal-100 border-2 border-teal-500"
              : completionState === "attention"
              ? isHighImpact
                ? "bg-white border-2 border-amber-500"
                : "bg-white border-2 border-slate-300"
              : "bg-white border-2 border-slate-300"
            }
          `}
          style={{
            boxShadow: "0 0 0 2px white",
            ...(isHighImpact ? { animation: "markerPing 2.5s ease-in-out infinite" } : {}),
          }}
        />

        <TickConnector visible={!isLast} />
      </div>

      {/* ── Card content ─────────────────────────────── */}
      <div
        className={`
          flex-1 min-w-0 transition-all duration-200
          ${isExpanded
            ? "mb-1.5 rounded-xl border border-slate-200 bg-white shadow-sm ring-1 ring-teal-500/5"
            : "mb-0"
          }
        `}
        style={isExpanded ? { borderLeft: "2px solid rgba(13,148,136,0.15)" } : undefined}
      >

        {/* ════════════════════════════════════════════ */}
        {/* COLLAPSED VIEW — Roadmap style              */}
        {/* ════════════════════════════════════════════ */}
        {!isExpanded && (
          <button
            onClick={() => !readOnly && setIsExpanded(true)}
            disabled={readOnly}
            className="w-full text-left py-[6px] pr-4 pl-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-inset rounded-lg cursor-pointer disabled:cursor-default hover:bg-slate-50/60 transition-colors"
          >
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                {/* Title row */}
                <div className="flex items-center gap-1">
                  <span className={`text-sm font-medium leading-tight truncate ${
                    completionState === "empty" ? "text-slate-400" : "text-slate-800"
                  }`}>
                    {event.title?.trim() ? display.displayTitle : (
                      <span className="italic font-normal">Add a title…</span>
                    )}
                  </span>
                  {completionState === "complete" && (
                    <CheckIcon className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />
                  )}
                </div>

                {/* Detail or nudge */}
                {completionState === "complete" && display.displaySnippet && (
                  <div className="text-xs text-slate-500 mt-0.5 truncate leading-relaxed">
                    {display.displaySnippet}
                  </div>
                )}
                {completionState === "attention" && attentionHint && (
                  <div className="flex items-center gap-1 mt-0.5">
                    {isHighImpact ? (
                      <>
                        <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200/80 rounded px-1.5 py-px leading-tight">High impact</span>
                        <span className="text-[11.5px] text-amber-700 font-medium">{attentionHint}</span>
                      </>
                    ) : (
                      <span className="text-[11.5px] text-slate-400 font-medium">{attentionHint}</span>
                    )}
                  </div>
                )}
              </div>

              {/* Right: actions + chevron */}
              <div className="flex items-center gap-0.5 flex-shrink-0 pt-0.5">
                {/* Desktop quick actions */}
                {!readOnly && (
                  <div className="hidden sm:flex items-center gap-0.5 opacity-0 group-hover/card:opacity-100 focus-within:opacity-100 transition-opacity duration-150">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEdit(); }}
                      className="p-1 text-slate-400 hover:text-teal-600 rounded-md hover:bg-teal-50 transition-colors"
                      aria-label="Edit event"
                    >
                      <EditIcon />
                    </button>
                    {onDuplicate && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDuplicate(); }}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-100 transition-colors"
                        aria-label="Duplicate"
                      >
                        <DuplicateIcon />
                      </button>
                    )}
                    <div className="relative">
                      <button
                        onClick={(e) => { e.stopPropagation(); setConfirmDelete(true); }}
                        className="p-1 text-slate-400 hover:text-red-500 rounded-md hover:bg-red-50 transition-colors"
                        aria-label="Delete"
                      >
                        <TrashIcon />
                      </button>
                      {confirmDelete && (
                        <DeleteConfirm onConfirm={handleDelete} onCancel={() => setConfirmDelete(false)} />
                      )}
                    </div>
                  </div>
                )}

                {/* Mobile overflow */}
                {!readOnly && (
                  <div className="sm:hidden">
                    <OverflowMenu
                      onEdit={handleEdit}
                      onAddDetails={handleAddDetails}
                      onDuplicate={onDuplicate ? handleDuplicate : undefined}
                      onRequestDelete={() => setConfirmDelete(true)}
                    />
                    {confirmDelete && (
                      <DeleteConfirm onConfirm={handleDelete} onCancel={() => setConfirmDelete(false)} />
                    )}
                  </div>
                )}

                {/* Chevron */}
                {!readOnly && (
                  <ChevronRight className="w-[15px] h-[15px] text-slate-300 group-hover/card:text-slate-400 transition-all ml-0.5 group-hover/card:translate-x-0.5" />
                )}
              </div>
            </div>
          </button>
        )}

        {/* ════════════════════════════════════════════ */}
        {/* EXPANDED VIEW (preserved from previous)     */}
        {/* ════════════════════════════════════════════ */}
        {isExpanded && !readOnly && (
          <div className="px-4 py-3.5">
            {/* Header row */}
            <div className="flex items-center justify-between mb-3.5">
              <div className="flex items-center gap-1.5">
                {eventTypeOption && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1">
                    <EventTypeIcon type={event.event_type} className="w-3.5 h-3.5" />
                    <span className="font-medium">{eventTypeOption.label}</span>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {onDuplicate && (
                  <button
                    onClick={handleDuplicate}
                    className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                    aria-label="Duplicate event"
                  >
                    <DuplicateIcon className="w-4 h-4" />
                  </button>
                )}
                <div className="relative">
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="p-1.5 text-slate-400 hover:text-slate-500 rounded-lg hover:bg-slate-100 transition-colors"
                    aria-label="Delete event"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                  {confirmDelete && (
                    <DeleteConfirm onConfirm={handleDelete} onCancel={() => setConfirmDelete(false)} />
                  )}
                </div>
                <div className="w-px h-4 bg-slate-200 mx-0.5" />
                <button
                  onClick={() => setIsExpanded(false)}
                  className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors"
                  aria-label="Collapse"
                >
                  <ChevronUp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Essential fields */}
            <div className="space-y-2.5">
              <input
                ref={titleRef}
                type="text"
                value={event.title}
                onChange={(e) => handleFieldChange("title", e.target.value)}
                placeholder={TYPE_PLACEHOLDERS[event.event_type] || "Brief description of this event…"}
                className="
                  w-full text-sm font-semibold text-slate-800 placeholder:text-slate-400 placeholder:font-normal
                  border-0 border-b border-slate-200 px-0 py-2
                  focus:outline-none focus:border-teal-400 transition-colors duration-150
                "
              />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center gap-1.5 mb-1">
                    <label htmlFor={`date-${event.id}`} className="text-xs font-medium text-slate-500">Date</label>
                    {event.date_precision === "approximate" && dateInput && (
                      <span className="text-xs text-teal-600 bg-teal-50 rounded px-1 py-px font-medium">Approx</span>
                    )}
                  </div>
                  <input
                    id={`date-${event.id}`}
                    type="text"
                    value={dateInput}
                    onChange={(e) => handleDateChange(e.target.value)}
                    placeholder="Jan 2026, ~6 weeks ago"
                    className="
                      w-full border border-slate-200 rounded-lg px-2.5 py-2 text-sm text-slate-700
                      placeholder:text-slate-400 hover:border-slate-300
                      focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400
                      transition-all duration-150
                    "
                  />
                </div>
                <div>
                  <label htmlFor={`type-${event.id}`} className="block text-xs font-medium text-slate-500 mb-1">Type</label>
                  <TypeSelect id={`type-${event.id}`} value={event.event_type} onChange={(val) => handleFieldChange("event_type", val)} />
                </div>
              </div>

              <div className="relative">
                <textarea
                  value={event.details || ""}
                  onChange={(e) => handleFieldChange("details", e.target.value.slice(0, 240))}
                  placeholder="Add context (optional)…"
                  rows={2}
                  maxLength={240}
                  className="
                    w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-700
                    placeholder:text-slate-400 hover:border-slate-300
                    focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400
                    transition-all duration-150 resize-none
                  "
                  style={{ minHeight: "3.5rem" }}
                />
                {showCharCount && (
                  <span className={`absolute bottom-2 right-2 text-xs ${detailsLength >= 230 ? "text-amber-500 font-medium" : "text-slate-400"}`}>
                    {detailsLength}/240
                  </span>
                )}
              </div>
            </div>

            {/* Supporting info disclosure */}
            <div className="mt-3.5">
              {!supportingOpen ? (
                <button
                  onClick={() => setSupportingOpen(true)}
                  className="
                    flex items-center gap-1.5 w-full px-3 py-2
                    rounded-lg border border-dashed border-slate-200
                    text-xs text-slate-500 font-medium
                    hover:border-slate-300 hover:text-slate-700 hover:bg-slate-50/50
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500
                    transition-all duration-150
                  "
                  aria-expanded="false"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Add supporting info
                  <span className="text-slate-400 font-normal ml-0.5">— optional</span>
                  {supportingCount > 0 && (
                    <span className="ml-auto text-xs bg-slate-100 text-slate-600 rounded px-1.5 py-0.5 font-medium">
                      {supportingCount} added
                    </span>
                  )}
                </button>
              ) : (
                <div className="rounded-lg border border-slate-200 bg-slate-50/50 overflow-hidden">
                  <button
                    onClick={() => setSupportingOpen(false)}
                    className="flex items-center gap-1.5 w-full px-3 py-2 text-xs text-slate-600 font-medium hover:bg-slate-100/50 transition-colors duration-150"
                    aria-expanded="true"
                  >
                    <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                    Supporting info
                    {supportingCount > 0 && (
                      <span className="ml-auto text-xs bg-white text-slate-500 rounded px-1.5 py-0.5 border border-slate-200">
                        {supportingCount} added
                      </span>
                    )}
                  </button>

                  <div className="px-3 pb-3 space-y-2.5">
                    <div>
                      <span className="block text-xs font-medium text-slate-500 mb-1.5">Outcome</span>
                      <div className="flex flex-wrap gap-1.5">
                        {OUTCOME_OPTIONS.map((opt) => (
                          <button
                            key={opt.value || "none"}
                            onClick={() => handleFieldChange("outcome", opt.value)}
                            className={`
                              px-2.5 py-1 text-xs rounded-lg border font-medium transition-all duration-150
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500
                              ${event.outcome === opt.value
                                ? "bg-teal-600 border-teal-600 text-white shadow-sm"
                                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:shadow-sm"
                              }
                            `}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <span className="block text-xs font-medium text-slate-500 mb-1.5">Evidence</span>
                      <div className="flex flex-wrap gap-1.5">
                        {EVIDENCE_FLAG_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => handleEvidenceFlagToggle(opt.value)}
                            className={`
                              px-2.5 py-1 text-xs rounded-lg border font-medium transition-all duration-150
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500
                              ${event.evidence_flags?.includes(opt.value)
                                ? "bg-teal-600 border-teal-600 text-white shadow-sm"
                                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:shadow-sm"
                              }
                            `}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <span className="block text-xs font-medium text-slate-500 mb-1.5">Functional Impact</span>
                      <div className="flex flex-wrap gap-1.5">
                        {FUNCTION_TAG_OPTIONS.map((opt) => (
                          <button
                            key={opt.value}
                            onClick={() => handleFunctionTagToggle(opt.value)}
                            className={`
                              px-2.5 py-1 text-xs rounded-lg border font-medium transition-all duration-150
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500
                              ${event.function_tags?.includes(opt.value)
                                ? "bg-teal-600 border-teal-600 text-white shadow-sm"
                                : "bg-white border-slate-200 text-slate-600 hover:border-slate-300 hover:shadow-sm"
                              }
                            `}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer: collapse */}
            <div className="mt-3.5 pt-2.5 border-t border-slate-100">
              <button
                onClick={() => setIsExpanded(false)}
                className="inline-flex items-center gap-1.5 text-xs text-slate-500 font-medium hover:text-slate-700 focus:outline-none focus-visible:underline transition-colors duration-150"
              >
                <ChevronUp className="w-3.5 h-3.5" />
                Collapse
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TimelineEventCard;